import { useState, useEffect } from 'react';
import { Grid, TextField, Button, CircularProgress } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import useCustomSnackbar from 'hooks/useCustomSnackbar';
import useProductsIM from 'hooks/useProductsIM';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useStyles from './IMProductForm.styles';
import { IProductForm, IProductResponse } from 'interfaces/imProducts.interface';
import { iProviders } from 'interfaces/imProviders.interface';
import { ICategories, ICategoriesPromotion } from 'interfaces/imCategories.interface';

interface Props {
  handleCloseModal(): void;
  handleOnSubmit(data: IProductForm, productId: string): Promise<boolean>;
  getAllProducts(): void;
  parentId: number;
  productId: string;
  providers: iProviders[];
  categories: ICategories[];
  categoriesPromo: ICategoriesPromotion[];
}

const defaultProvider: any = {
  id: '',
  name: 'Selecciona un proveedor'
};
const defaultCategory: any = {
  id: '',
  name: 'Selecciona una categoría'
};
const defaultCategoryProm: any = {
  id: '',
  name: 'Selecciona una categoría promoción'
};

export default function IMProductForm({
  handleOnSubmit,
  handleCloseModal,
  productId,
  getAllProducts,
  parentId,
  providers,
  categories,
  categoriesPromo
}: Props) {
  const { showNotistack } = useCustomSnackbar();
  const classes = useStyles();
  const { handleSubmit, register, errors, reset } = useForm<IProductForm>();
  const { getProductById } = useProductsIM();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [provider, setProvider] = useState(defaultProvider);
  const [category, setCategory] = useState(defaultCategory);
  const [categoryPromo, setCategoryPromo] = useState(defaultCategoryProm);

  const getProduct = () => {
    setIsLoadingProduct(true);
    if (productId) {
      getProductById(productId)
        .then((resultProduct: IProductResponse) => {
          const findProvider = providers.find((provider) => provider.id === resultProduct.providerId);
          const findCategory = categories.find((category) => category.id === resultProduct.categoryId);
          const findCategoryPromo = categoriesPromo.find(
            (categoryPromo) => categoryPromo.id === resultProduct.categoryPromoId
          );

          setProvider(findProvider);
          setCategory(findCategory);
          setCategoryPromo(findCategoryPromo);
          reset(resultProduct);
        })
        .finally(() => {
          setIsLoadingProduct(false);
        });
    }
  };

  const onSubmit = (data: IProductForm) => {
    setFormDirty(true);
    data.minStock = Number(data.minStock);
    data.maxStock = Number(data.maxStock);
    data.minOrderQuantity = Number(data.minOrderQuantity);
    data['category'] = category.id;
    if (!provider?.id) {
      showNotistack({ message: 'Debes seleccionar el Proveedor' });
      return;
    }
    data['provider'] = provider.id;
    data['categoryPromo'] = categoryPromo.id;
    data['parentId'] = parentId;
    data['productId'] = Number(productId);

    setIsLoading(true);
    handleOnSubmit(data, productId)
      .then((response: boolean) => {
        if (response === true) {
          showNotistack({ message: 'El producto se guardó correctamente' });
          getAllProducts();
          handleCloseModal();
        } else {
          showNotistack({ message: 'Ocurrió un error' });
        }
      })
      .catch((err: any) => {
        console.log(err.reponse?.data);
        showNotistack({ message: 'Ocurrió un error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getProduct();
  }, [productId]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Grid container direction="row" spacing={2} justify="center">
        <Grid item xs={12} sm={12} md={12}>
          <Autocomplete
            value={provider}
            fullWidth
            getOptionLabel={(option: any) => (option.name ? option.name : '')}
            onChange={(_, value: string | null) => {
              setProvider(value ? value : '');
            }}
            id="providers"
            options={providers}
            className={classes.marginFields}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Proveedores"
                name="providers"
                disabled={isLoading}
                error={formDirty && !provider}
                helperText={formDirty && !provider ? 'Este campo es requerido' : ''}
                variant="outlined"
              />
            )}
          />
          <Grid container direction="row" spacing={2} justify="center">
            <Grid item xs={12} sm={12} md={6}>
              <Autocomplete
                value={category}
                fullWidth
                getOptionLabel={(option: any) => (option.name ? option.name : '')}
                onChange={(_, value: string | null) => {
                  setCategory(value ? value : '');
                }}
                id="categories"
                className={classes.marginFields}
                options={categories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Categorías"
                    name="categories"
                    disabled={isLoading}
                    error={formDirty && !category}
                    helperText={formDirty && !category ? 'Este campo es requerido' : ''}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Autocomplete
                value={categoryPromo}
                fullWidth
                getOptionLabel={(option: any) => (option.name ? option.name : '')}
                onChange={(_, value: string | null) => {
                  setCategoryPromo(value ? value : '');
                }}
                id="categoriesPromo"
                className={classes.marginFields}
                options={categoriesPromo}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Categorías Promo"
                    name="categoriesPromo"
                    disabled={isLoading}
                    error={formDirty && !categoryPromo}
                    helperText={formDirty && !categoryPromo ? 'Este campo es requerido' : ''}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container direction="row" spacing={2} justify="center">
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                required
                variant="outlined"
                margin="normal"
                fullWidth
                name="minStock"
                label="Stock mínimo"
                autoComplete="minStock"
                disabled={isLoading}
                inputRef={register<HTMLInputElement>({
                  required: 'Este campo es requerido',
                  pattern: {
                    value: /^\d*[1-9]\d*$/,
                    message: 'Stock mínimo invalido'
                  }
                })}
                error={errors.minStock ? true : false}
                helperText={errors.minStock && errors.minStock.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                required
                variant="outlined"
                margin="normal"
                fullWidth
                name="maxStock"
                label="Stock máximo"
                autoComplete="maxStock"
                disabled={isLoading}
                inputRef={register<HTMLInputElement>({
                  required: 'Este campo es requerido',
                  pattern: {
                    value: /^\d*[1-9]\d*$/,
                    message: 'Stock máximo invalido'
                  }
                })}
                error={errors.maxStock ? true : false}
                helperText={errors.maxStock && errors.maxStock.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                required
                variant="outlined"
                margin="normal"
                fullWidth
                name="minOrderQuantity"
                label="Cantidad mínima(MOQ)"
                autoComplete="minOrderQuantity"
                disabled={isLoading}
                inputRef={register<HTMLInputElement>({
                  required: 'Este campo es requerido',
                  pattern: {
                    value: /^\d*[1-9]\d*$/,
                    message: 'Cantidad mínima invalido'
                  }
                })}
                error={errors.minOrderQuantity ? true : false}
                helperText={errors.minOrderQuantity && errors.minOrderQuantity.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Button
          type="submit"
          onClick={() => {
            setFormDirty(true);
          }}
          variant="contained"
          color="primary"
          disabled={isLoadingProduct || isLoading}
          style={{ marginTop: 20 }}
        >
          {isLoadingProduct || isLoading ? <CircularProgress size={24} /> : ''} Guardar
        </Button>
        <Button
          variant="contained"
          color="default"
          disabled={isLoadingProduct || isLoading}
          style={{ marginTop: 20, marginLeft: 20 }}
          onClick={handleCloseModal}
        >
          {isLoadingProduct || isLoading ? <CircularProgress size={24} /> : ''} Cancelar
        </Button>
      </Grid>
    </form>
  );
}
